import React from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useAuth } from '@clerk/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { convex } from '../lib/convex';
import { ThemeProvider } from 'next-themes';
import { TemplateProvider } from '@template/providers/TemplateProvider';
import { env } from '../env';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import i18n from '@/i18n';
import { CookieBanner } from '@/components/ui/cookie-banner';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { identifyUser, normalizeAnalyticsRole, trackPageView as trackPostHogPageView } from '@/lib/analytics';
import { trackPageView as trackGTMPageView } from '@/lib/gtm';
import { getRouteAnalyticsSnapshot } from '@/lib/route-analytics';
import { resetPostHog } from '@/lib/posthog';

const PUBLISHABLE_KEY = env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error('Add your Clerk Publishable Key to the .env.local file');
}

// Dev-only: load the router devtools lazily so the chunk never enters the
// production graph (the static import previously shipped ~64 kB to prod).
// Production-first ordering lets the bundler statically drop the dynamic
// import() during dead-code elimination; import.meta.env.PROD is the
// browser-native flag Vite replaces with a literal at build time.
const TanStackRouterDevtools = import.meta.env.PROD
  ? (): null => null
  : React.lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      }))
    );

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

interface CurrentUserEffectsSnapshot {
  _id?: string;
  preferredLanguage?: 'de' | 'en';
  role?: unknown;
  isMvz?: boolean;
  onboardingComplete?: boolean;
  profileComplete?: boolean;
}

// Sync current-user side effects from one Convex subscription.
const CurrentUserEffects: React.FC = () => {
  const { isSignedIn, userId } = useAuth();
  const convexUser = useQuery(api.template.users.getCurrentUser as any) as CurrentUserEffectsSnapshot | null | undefined;
  const hasSyncedLanguageRef = React.useRef(false);
  const identifiedKeyRef = useRef<string | null>(null);

  React.useEffect(() => {
    if (convexUser && !hasSyncedLanguageRef.current) {
      const dbLanguage = convexUser.preferredLanguage;
      if (dbLanguage && dbLanguage !== i18n.language) {
        i18n.changeLanguage(dbLanguage);
        localStorage.setItem('i18nextLng', dbLanguage);
        hasSyncedLanguageRef.current = true;
      }
    }
  }, [convexUser]);

  useEffect(() => {
    if (!isSignedIn || !userId) {
      if (identifiedKeyRef.current) {
        resetPostHog();
        identifiedKeyRef.current = null;
      }
      return;
    }

    const stableUserId = convexUser?._id ?? userId;
    const role = normalizeAnalyticsRole(convexUser?.role);
    const nextKey = [stableUserId, role, convexUser?.isMvz, convexUser?.onboardingComplete, convexUser?.profileComplete].join(':');

    if (identifiedKeyRef.current === nextKey) {
      return;
    }

    identifyUser(stableUserId, {
      role,
      is_mvz: convexUser?.isMvz,
      onboarding_complete: convexUser?.onboardingComplete,
      profile_complete: convexUser?.profileComplete,
    });
    identifiedKeyRef.current = nextKey;
  }, [convexUser?._id, convexUser?.isMvz, convexUser?.onboardingComplete, convexUser?.profileComplete, convexUser?.role, isSignedIn, userId]);

  return null;
};

// Track pageviews for PostHog analytics
const PageViewTracker: React.FC = () => {
  const router = useRouterState();
  const location = router.location;
  const hasSeenInitialRouteRef = useRef(false);
  const previousUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!location.pathname) return;

    const { pagePath, route, search } = getRouteAnalyticsSnapshot(location);
    const pageTitle = typeof document !== 'undefined' ? document.title : pagePath;
    const pageUrl = typeof window !== 'undefined' ? window.location.href : pagePath;

    trackPostHogPageView(pagePath, {
      route,
      search,
    });

    if (hasSeenInitialRouteRef.current) {
      trackGTMPageView(pagePath, pageTitle, pageUrl, previousUrlRef.current ?? (typeof document !== 'undefined' ? document.referrer : undefined));
    } else {
      hasSeenInitialRouteRef.current = true;
    }

    previousUrlRef.current = pageUrl;
  }, [location.href, location.pathname, location.searchStr]);

  return null;
};

const RootComponent: React.FC = () => {
  // Sync <html lang> with i18n and lazy-load Clerk localization
  const initialLang = React.useMemo(() => {
    if (typeof window === 'undefined') return 'en';
    const stored = localStorage.getItem('i18nextLng');
    if (stored === 'de' || stored === 'en') return stored;
    const nav = navigator.language?.toLowerCase();
    return nav?.startsWith('de') ? 'de' : 'en';
  }, []);

  const [currentLang, setCurrentLang] = React.useState(initialLang);
  const [clerkLocale, setClerkLocale] = React.useState<any>(undefined);

  React.useEffect(() => {
    let i18nInstance: any;
    let unsubscribe: (() => void) | undefined;
    void import('../i18n').then((mod) => {
      i18nInstance = mod.default;
      setCurrentLang(i18nInstance.language);
      const onChange = (lng: string) => setCurrentLang(lng);
      i18nInstance.on('languageChanged', onChange);
      unsubscribe = () => i18nInstance.off('languageChanged', onChange);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', currentLang === 'de' ? 'de' : 'en');
    }
  }, [currentLang]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Import only the active locale's subpath (~80 kB) instead of the
        // barrel, which bundles all ~60 Clerk locales (~5 MB).
        const locale =
          currentLang === 'de'
            ? (await import('@clerk/localizations/de-DE')).deDE
            : (await import('@clerk/localizations/en-US')).enUS;
        if (!cancelled) setClerkLocale(locale);
      } catch {
        if (!cancelled) setClerkLocale(undefined);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [currentLang]);

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl='/auth/sign-in'
      localization={clerkLocale}
      // We build our own auth UI with hooks; skip prefetching the @clerk/ui
      // bundle from the CDN. Prebuilt components (e.g. <CreateOrganization />)
      // still load it on demand when they mount.
      prefetchUI={false}
      appearance={{
        variables: {
          colorBackground: 'oklch(var(--background) / 1)',
          colorForeground: 'oklch(var(--foreground) / 1)',
          colorMuted: 'oklch(var(--muted) / 1)',
          colorMutedForeground: 'oklch(var(--muted-foreground) / 1)',
          colorNeutral: 'oklch(var(--muted) / 1)',
          colorPrimary: 'oklch(var(--primary) / 1)',
          colorPrimaryForeground: 'oklch(var(--primary-foreground) / 1)',
          colorInput: 'oklch(var(--input) / 1)',
          colorInputForeground: 'oklch(var(--foreground) / 1)',
          colorBorder: 'oklch(var(--border) / 1)',
          colorRing: 'oklch(var(--ring) / 1)',
          colorSuccess: 'oklch(var(--accent) / 1)',
          colorWarning: 'oklch(80% 0.25 80 / 1)',
          colorDanger: 'oklch(var(--destructive) / 1)',
          colorModalBackdrop: 'oklch(0% 0 0 / 0.5)',
          colorShadow: 'oklch(var(--foreground) / 0.1)',
        },
        elements: {
          rootBox: 'z-[1000]',
          modalBackdrop: 'bg-[oklch(0%_0_0)]/60 backdrop-blur-sm',
          card: 'bg-background text-foreground shadow-2xl border border-border',
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <TemplateProvider>
            <ThemeProvider attribute='class' defaultTheme='light' enableSystem={false}>
              <>
                <CurrentUserEffects />
                <PageViewTracker />
                <Outlet />
                <CookieBanner />
                {!import.meta.env.PROD && (
                  <React.Suspense fallback={null}>
                    <TanStackRouterDevtools position='bottom-right' />
                  </React.Suspense>
                )}
              </>
            </ThemeProvider>
          </TemplateProvider>
        </ConvexProviderWithClerk>
      </QueryClientProvider>
    </ClerkProvider>
  );
};

export const Route = createRootRoute({
  loader: async () => {
    // Ensure core namespaces are loaded before rendering routes
    const { default: i18n } = await import('../i18n');
    await i18n.loadNamespaces?.(['common', 'nav']);
    return null;
  },
  component: RootComponent,
});
